import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { InActionWord } from 'app/types';

interface SourceTextDialogProps {
  open: boolean;
  onClose: () => void;
  text: string;
  words: InActionWord[];
}

interface Segment {
  text: string;
  group?: InActionWord['analysisGroup'];
}

const groupStyle: Record<
  NonNullable<InActionWord['analysisGroup']>,
  { bg: string; color: string; border: string }
> = {
  LEARNED_ACTIVE: {
    bg: 'rgba(39, 204, 188, 0.18)',
    color: '#1F8A7E',
    border: 'rgba(39, 204, 188, 0.5)',
  },
  NEEDS_REPEAT: {
    bg: 'rgba(226, 166, 16, 0.18)',
    color: '#A87800',
    border: 'rgba(226, 166, 16, 0.5)',
  },
  NOT_LEARNED_IN_VOCABULARY: {
    bg: 'rgba(59, 44, 147, 0.12)',
    color: '#3B2C93',
    border: 'rgba(59, 44, 147, 0.4)',
  },
  NOT_IN_VOCABULARY: {
    bg: 'rgba(226, 51, 10, 0.14)',
    color: '#B8260A',
    border: 'rgba(226, 51, 10, 0.4)',
  },
};

// Tokenize the text and attach analysisGroup metadata where the token matches
// a word in the list. Naive lowercase match — good enough for the visual mock.
const tokenize = (text: string, words: InActionWord[]): Segment[] => {
  const wordMap = new Map<string, InActionWord['analysisGroup']>();
  words.forEach((w) => {
    if (!w.word) return;
    if (!wordMap.has(w.word.toLowerCase())) {
      wordMap.set(w.word.toLowerCase(), w.analysisGroup);
    }
  });

  const segments: Segment[] = [];
  const parts = text.split(/(\s+|[.,!?"]+)/);
  parts.forEach((part) => {
    const clean = part.toLowerCase().replace(/[^a-z']/g, '');
    if (clean && wordMap.has(clean)) {
      segments.push({ text: part, group: wordMap.get(clean) });
    } else {
      segments.push({ text: part });
    }
  });
  return segments;
};

const SourceTextDialog: React.FC<SourceTextDialogProps> = ({
  open,
  onClose,
  text,
  words,
}) => {
  const segments = useMemo(() => tokenize(text, words), [text, words]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={'md'}
      disableScrollLock
    >
      <DialogTitle
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant={'h4'}>{'Оригінальний текст'}</Typography>
          <Typography variant={'body2'} color={'text.secondary'} sx={{ mt: 0.5 }}>
            {'Слова підсвічено за статусом.'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size={'small'}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction={'row'} spacing={0.75} flexWrap={'wrap'} sx={{ mb: 2 }}>
          <Legend group={'NOT_IN_VOCABULARY'} label={'Нові / без перекладу'} />
          <Legend group={'NOT_LEARNED_IN_VOCABULARY'} label={'Не вивчені'} />
          <Legend group={'NEEDS_REPEAT'} label={'Повторення'} />
          <Legend group={'LEARNED_ACTIVE'} label={'Знайомі'} />
        </Stack>

        <Typography
          component={'div'}
          sx={{ fontSize: 16, lineHeight: 1.8 }}
        >
          {segments.map((seg, i) =>
            seg.group ? (
              <Box
                key={i}
                component={'span'}
                sx={{
                  bgcolor: groupStyle[seg.group].bg,
                  borderBottom: `2px solid ${groupStyle[seg.group].border}`,
                  borderRadius: '3px',
                  color: groupStyle[seg.group].color,
                  fontWeight: 600,
                  px: 0.5,
                }}
              >
                {seg.text}
              </Box>
            ) : (
              <React.Fragment key={i}>{seg.text}</React.Fragment>
            ),
          )}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

const Legend: React.FC<{
  group: NonNullable<InActionWord['analysisGroup']>;
  label: string;
}> = ({ group, label }) => (
  <Chip
    label={label}
    size={'small'}
    sx={{
      bgcolor: groupStyle[group].bg,
      color: groupStyle[group].color,
      border: `1px solid ${groupStyle[group].border}`,
      fontWeight: 600,
      height: 22,
    }}
  />
);

export default SourceTextDialog;
